import omit from 'lodash/omit';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { ACTION, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { AGENT_REMOTE_EDIT_REVIEW_DATA } from '@atlaskit/editor-common/collab-agent-remote-edit-review';
import type { AgentRemoteEditReviewData } from '@atlaskit/editor-common/collab-agent-remote-edit-review';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';

import type { AIPlugin } from '../../aiPluginType';
import { convertExperienceName } from '../../ui/utils/analytics/analyticsFlow';
import { addAnalytics } from '../../ui/utils/analytics/utils';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';

import { aiMonitorWatchmenPluginKey } from './ai-monitor-watchmen-plugin-key';
import type {
	AIMonitorWatchmenPluginState,
	AnalyticsToDefer,
	CommonAttributes,
	DeferredAnalyticsState,
} from './ai-monitor-watchmen-plugin-key';
import type { Session } from './types';
import {
	collectChangedRangesFromMapping,
	countSessionReductions,
	extendBackendPersistedRanges,
	finishImmediateReaction,
	isExactInverseOfSession,
	isRedoOfUndoneSession,
	mergeRanges,
	sumTextCounts,
	toBackendPersistedRanges,
	txnTouchesAiRanges,
} from './utils';

const META_FLUSH_ANALYTICS = 'AIMonitorWatchmenFlushAnalytics';
const META_DEFERRED_ANALYTICS = 'AIMonitorWatchmenDeferredAnalytics';
export const META_UPDATE_COMMON_ATTRIBUTES = 'AIMonitorWatchmenCommonAttributes';
const ACTIONED_EVENT_DELAY = 30000;

/** Synthetic id keeps backend actioned and discarded events queryable. */
const BACKEND_UPDATE_SESSION_ID_PREFIX = 'backendUpdate_';
const BACKEND_UPDATE_EXPERIENCE_NAME = 'backendAgentEdit';
const BACKEND_UPDATE_ACTIONED_TYPE = 'backendPersisted';

/** Prevents a backend session from being counted as both accepted and discarded. */
const acceptIsStillPending = (session: Session): boolean =>
	session.acceptPendingUntil === undefined || performance.now() < session.acceptPendingUntil;

/**
 * Upserts one session without displacing deferrals from the other route. Successive
 * frontend streams still collapse into one event.
 */
const upsertDeferral = (
	existing: AnalyticsToDefer[] | undefined,
	entry: AnalyticsToDefer,
): AnalyticsToDefer[] => [
	...(existing ?? []).filter((d) => {
		if (d.aiSessionId === entry.aiSessionId) {
			return false;
		}
		return entry.isBackendPersisted || d.isBackendPersisted;
	}),
	entry,
];

const dropPendingBackendSession = (
	state: AIMonitorWatchmenPluginState,
	sessionId: Session['id'],
): AIMonitorWatchmenPluginState => ({
	...state,
	currentSession: null,
	pendingAnalytics: null,
	deferredAnalytics: {
		...state.deferredAnalytics,
		toDefer: state.deferredAnalytics.toDefer?.filter((entry) => entry.aiSessionId !== sessionId),
		toDrop: sessionId,
	},
});

const isAppendedToRemoteTransaction = (transaction: ReadonlyTransaction): boolean => {
	let origin = transaction.getMeta('appendedTransaction') as ReadonlyTransaction | undefined;
	while (origin) {
		if (origin.getMeta('isRemote')) {
			return true;
		}
		origin = origin.getMeta('appendedTransaction') as ReadonlyTransaction | undefined;
	}
	return false;
};

const initialState: AIMonitorWatchmenPluginState = {
	currentSession: null,
	pendingAnalytics: null,
	lastUndoneSession: null,
	deferredAnalytics: { deferred: {}, sessionState: 'notStarted' },
	commonAttributes: {},
};

export const createAIMonitorWatchmenPlugin = (
	api: ExtractInjectionAPI<AIPlugin> | undefined,
	autoApplyAIChangesEnabled: () => boolean,
): SafePlugin => {
	return new SafePlugin<AIMonitorWatchmenPluginState>({
		key: aiMonitorWatchmenPluginKey,
		state: {
			init: () => initialState,
			apply: (tr, pluginState, _old, newState) => {
				let sessionStarted = false;
				const decorationPluginState = aiExperienceDecorationPluginKey.getState(newState);
				sessionStarted = decorationPluginState?.sessionStarted ?? false;
				const { deferredAnalytics } = pluginState;

				if (sessionStarted && deferredAnalytics.sessionState !== 'inProgress') {
					return {
						...pluginState,
						deferredAnalytics: { ...deferredAnalytics, sessionState: 'inProgress' },
					};
				}

				const sessionEnded = decorationPluginState?.sessionEnded ?? false;
				if (sessionEnded && deferredAnalytics.sessionState === 'inProgress') {
					if (tr.getMeta(aiExperienceDecorationPluginKey)?.data?.type === 'finished') {
						return {
							...pluginState,
							deferredAnalytics: { ...deferredAnalytics, sessionState: 'justEnded' },
						};
					} else {
						return {
							...pluginState,
							deferredAnalytics: {
								...deferredAnalytics,
								toCancel: decorationPluginState?.singleInstrumentationId,
							},
						};
					}
				}

				const deferredAnalyticsFromMeta: DeferredAnalyticsState | undefined =
					tr.getMeta(META_DEFERRED_ANALYTICS);

				if (deferredAnalyticsFromMeta) {
					return { ...pluginState, deferredAnalytics: deferredAnalyticsFromMeta };
				}

				const updatedCommonAttributes: CommonAttributes | undefined = tr.getMeta(
					META_UPDATE_COMMON_ATTRIBUTES,
				);

				if (updatedCommonAttributes) {
					return { ...pluginState, commonAttributes: updatedCommonAttributes };
				}

				if (tr.getMeta(META_FLUSH_ANALYTICS)) {
					return { ...pluginState, pendingAnalytics: null };
				}

				const isCollab = Boolean(tr.getMeta('isRemote'));
				const isFinalAI = Boolean(tr.getMeta('isAIFinalStreamingTransformation'));
				const appendedTr = tr.getMeta('appendedTransaction');
				const isIntermediateAI = Boolean(
					tr.getMeta('isAIStreamingTransformation') ||
					(appendedTr && appendedTr.getMeta('isAIStreamingTransformation') && !isFinalAI),
				);

				const aiDecorationExperiencePluginState =
					api?.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState;
				const configItemKey = aiDecorationExperiencePluginState?.configItem?.key || '';
				const invokedFrom = aiDecorationExperiencePluginState?.lastTriggeredFrom;
				const experienceName = convertExperienceName(configItemKey, invokedFrom);

				// ignore txns that don't change the doc
				if (!tr.docChanged || tr.getMeta('metricsPlugin$')) {
					return pluginState;
				}

				// start a new analytic session when a stream finalizes
				if (isFinalAI) {
					const changed = collectChangedRangesFromMapping(tr.mapping.maps);
					const mergedNewRanges = mergeRanges(
						changed.map((r) => ({ from: r.newStart, to: r.newEnd })).filter((r) => r.from < r.to),
					);
					const { chars, words } = sumTextCounts(newState.doc, mergedNewRanges);
					const decorationPluginState = aiExperienceDecorationPluginKey.getState(newState);

					const session: Session = {
						// If `singleInstrumentationId` exists then we've been invoked by a free gen session,
						// otherwise we use `interactionSessionId` which comes from quick actions.
						// If none of these are available we will need to query for the `empty` events
						// Note we always need a valid ID
						id:
							decorationPluginState?.singleInstrumentationId ??
							decorationPluginState?.interactionSessionId ??
							// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
							`empty_${uuid()}`,
						startedAt: Math.round(performance.now()),
						ranges: changed,
						mergedNewRanges,
						baselineDoc: newState.doc,
						aiOutputChars: chars,
						aiOutputWords: words,
						awaitingNextAction: true,
						aiGroupId: decorationPluginState?.nextSessionId,
						experienceName,
					};

					let updatedDeferredAnalytics = pluginState.deferredAnalytics;

					if (autoApplyAIChangesEnabled()) {
						/**
						 * If traceId is present in decoration plugin state, use it, otherwise use traceIds from common attributes.
						 * Currently, traceId is set in decoration plugin state only for freegen.
						 * For other prompts, it should be in common attributes.
						 */
						const traceIds = decorationPluginState?.traceId
							? [decorationPluginState.traceId]
							: pluginState.commonAttributes.traceIds;
						const promptVersion = fg('platform_editor_ai_promptversion_in_analytics')
							? decorationPluginState?.promptVersion
							: undefined;
						const actionedPayload = createUnifiedAnalyticsPayload({
							...pluginState.commonAttributes,
							action: ACTION.ACTIONED,
							attributes: {
								aiResultAction: 'accepted',
								aiActionedType: 'automaticallyInserted',
							},
							interactionID: session.id,
							experienceName,
							invokedFrom,
							aiGroupId: decorationPluginState?.nextSessionId,
							isPayloadCreate: decorationPluginState?.isCreate,
							...{ traceIds },
							...(promptVersion ? { promptVersion } : {}),
						});

						updatedDeferredAnalytics = {
							...pluginState.deferredAnalytics,
							toDefer: upsertDeferral(pluginState.deferredAnalytics.toDefer, {
								aiSessionId: session.id,
								payload: actionedPayload,
								waitForSessionEnd: pluginState.deferredAnalytics.sessionState === 'inProgress',
							}),
						};
					}

					return {
						...pluginState,
						currentSession: session,
						pendingAnalytics: null,
						deferredAnalytics: updatedDeferredAnalytics,
					};
				}

				// Backend edits arrive as remote collab steps, so they need an explicit
				// watchmen session to participate in the existing survival metric.
				const agentRemoteEdit = tr.getMeta(AGENT_REMOTE_EDIT_REVIEW_DATA) as
					| AgentRemoteEditReviewData
					| undefined;
				// Upstream BE-streaming and agent-type gates control whether this meta exists.
				// Only the requester's client records because every collaborator gets the steps.
				if (agentRemoteEdit?.isLocalUserRequester) {
					const batchRanges = toBackendPersistedRanges(
						agentRemoteEdit.segments,
						newState.doc.content.size,
					);

					const activeSession = pluginState.currentSession;
					// Fold only non-terminal batches while the previous accept is pending.
					const now = performance.now();

					// Backend edits are already applied. Do not reuse frontend common attributes,
					// which may describe an unrelated earlier interaction.
					const buildActionedPayload = (interactionID: string) =>
						createUnifiedAnalyticsPayload({
							action: ACTION.ACTIONED,
							attributes: {
								aiResultAction: 'accepted',
								aiActionedType: BACKEND_UPDATE_ACTIONED_TYPE,
								aiGroupId: undefined,
								...(agentRemoteEdit.agentId ? { agentId: agentRemoteEdit.agentId } : {}),
							},
							interactionID,
							experienceName: BACKEND_UPDATE_EXPERIENCE_NAME,
						});

					if (
						activeSession?.isBackendPersisted &&
						activeSession.awaitingNextAction &&
						activeSession.backendEditComplete !== true &&
						acceptIsStillPending(activeSession)
					) {
						const combinedRanges = extendBackendPersistedRanges(
							activeSession.mergedNewRanges,
							batchRanges,
							(pos, bias) => tr.mapping.map(pos, bias),
							newState.doc.content.size,
						);
						if (!combinedRanges.length) {
							return dropPendingBackendSession(pluginState, activeSession.id);
						}

						const combinedCounts = sumTextCounts(newState.doc, combinedRanges);

						// Measure survival from the latest batch, matching frontend streaming.
						return {
							...pluginState,
							currentSession: {
								...activeSession,
								acceptPendingUntil: now + ACTIONED_EVENT_DELAY,
								backendEditComplete: agentRemoteEdit.complete === true,
								mergedNewRanges: combinedRanges,
								baselineDoc: newState.doc,
								aiOutputChars: combinedCounts.chars,
								aiOutputWords: combinedCounts.words,
							},
							deferredAnalytics: {
								...pluginState.deferredAnalytics,
								toDefer: upsertDeferral(pluginState.deferredAnalytics.toDefer, {
									aiSessionId: activeSession.id,
									isBackendPersisted: true,
									payload: buildActionedPayload(activeSession.id),
									waitForSessionEnd: false,
								}),
							},
						};
					}

					// A standalone pure deletion leaves no output for the survival metric.
					if (!batchRanges.length) {
						return pluginState;
					}

					const { chars, words } = sumTextCounts(newState.doc, batchRanges);
					const session: Session = {
						// Replace with the originating interaction id once it reaches the step contract.
						// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
						id: `${BACKEND_UPDATE_SESSION_ID_PREFIX}${uuid()}`,
						startedAt: Math.round(now),
						acceptPendingUntil: now + ACTIONED_EVENT_DELAY,
						backendEditComplete: agentRemoteEdit.complete === true,
						// Remote steps are not in local history, so backend edits cannot be undone here.
						ranges: [],
						mergedNewRanges: batchRanges,
						baselineDoc: newState.doc,
						aiOutputChars: chars,
						aiOutputWords: words,
						awaitingNextAction: true,
						aiGroupId: undefined,
						experienceName: BACKEND_UPDATE_EXPERIENCE_NAME,
						isBackendPersisted: true,
					};

					return {
						...pluginState,
						currentSession: session,
						pendingAnalytics: null,
						deferredAnalytics: {
							...pluginState.deferredAnalytics,
							toDefer: upsertDeferral(pluginState.deferredAnalytics.toDefer, {
								aiSessionId: session.id,
								isBackendPersisted: true,
								payload: buildActionedPayload(session.id),
								// The FE session lifecycle never starts for this route, so waiting
								// on it would defer the event forever.
								waitForSessionEnd: false,
							}),
						},
					};
				}

				// Editor normalization appended to remote steps is not a user reaction.
				if (pluginState.currentSession?.isBackendPersisted && isAppendedToRemoteTransaction(tr)) {
					const session = pluginState.currentSession;
					const mappedRanges = extendBackendPersistedRanges(
						session.mergedNewRanges,
						[],
						(pos, bias) => tr.mapping.map(pos, bias),
						newState.doc.content.size,
					);

					if (!mappedRanges.length) {
						return dropPendingBackendSession(pluginState, session.id);
					}

					const counts = sumTextCounts(newState.doc, mappedRanges);
					return {
						...pluginState,
						currentSession: {
							...session,
							aiOutputChars: counts.chars,
							aiOutputWords: counts.words,
							baselineDoc: newState.doc,
							mergedNewRanges: mappedRanges,
						},
					};
				}

				// observe and classify the very next local user action
				if (
					pluginState.currentSession &&
					pluginState.currentSession.awaitingNextAction &&
					!isCollab &&
					tr.docChanged
				) {
					const s = pluginState.currentSession;

					const isRegen = isIntermediateAI;

					if (isRegen) {
						return finishImmediateReaction(pluginState, s, 'regen', newState.doc);
					}

					const isUndo = isExactInverseOfSession(tr.mapping.maps, s.ranges);

					if (isUndo) {
						// Store the undone session for potential redo detection
						const undoneSession = {
							aiSessionId: s.id,
							undoneRanges: s.ranges,
							aiOutputChars: s.aiOutputChars,
							aiOutputWords: s.aiOutputWords,
							aiGroupId: s.aiGroupId,
							experienceName: s.experienceName,
							undoneAt: Math.round(performance.now()),
						};

						let updatedDeferredAnalytics = pluginState.deferredAnalytics;

						if (acceptIsStillPending(s)) {
							updatedDeferredAnalytics = {
								...pluginState.deferredAnalytics,
								toCancel: s.id,
								toCancelExperienceName: s.experienceName,
								toCancelIsBackendPersisted: s.isBackendPersisted,
							};
						}

						return {
							...finishImmediateReaction(pluginState, s, 'undo', newState.doc, {
								removedChars: s.aiOutputChars,
								removedWords: s.aiOutputWords,
								touched: s.mergedNewRanges.length,
							}),
							deferredAnalytics: updatedDeferredAnalytics,
							lastUndoneSession: undoneSession,
						};
					}

					if (!txnTouchesAiRanges(tr.mapping.maps, s.mergedNewRanges)) {
						return finishImmediateReaction(pluginState, s, 'other', newState.doc);
					}

					const { deletedChars, deletedWords, touched } = countSessionReductions(
						tr.steps,
						tr.mapping.maps,
						s.baselineDoc,
						s.mergedNewRanges,
					);
					const isDelete = deletedChars > 0 || deletedWords > 0;

					const overHalfDeleted = deletedChars >= s.aiOutputChars / 2;

					let updatedDeferredAnalytics = pluginState.deferredAnalytics;

					if (overHalfDeleted && acceptIsStillPending(s)) {
						updatedDeferredAnalytics = {
							...pluginState.deferredAnalytics,
							toCancel: s.id,
							toCancelExperienceName: s.experienceName,
							toCancelIsBackendPersisted: s.isBackendPersisted,
						};
					}

					return {
						...finishImmediateReaction(
							pluginState,
							s,
							isDelete ? 'delete' : 'other',
							newState.doc,
							{
								removedChars: deletedChars,
								removedWords: deletedWords,
								touched,
							},
							overHalfDeleted,
						),
						deferredAnalytics: updatedDeferredAnalytics,
					};
				}

				// Check for redo of previously undone AI content
				if (pluginState.lastUndoneSession && !isCollab && tr.docChanged && !isIntermediateAI) {
					const undoneSession = pluginState.lastUndoneSession;
					const now = Math.round(performance.now());
					const timeSinceUndo = now - undoneSession.undoneAt;

					// Check if this is a redo within a reasonable time window (2000ms like undo)
					if (timeSinceUndo <= 2000) {
						const isRedo = isRedoOfUndoneSession(tr.mapping.maps, undoneSession.undoneRanges);

						if (isRedo) {
							// Create a mock session for analytics purposes
							const mockSession: Session = {
								id: undoneSession.aiSessionId,
								startedAt: undoneSession.undoneAt,
								ranges: undoneSession.undoneRanges,
								mergedNewRanges: undoneSession.undoneRanges.map((r) => ({
									from: r.newStart,
									to: r.newEnd,
								})),
								baselineDoc: newState.doc,
								aiOutputChars: undoneSession.aiOutputChars,
								aiOutputWords: undoneSession.aiOutputWords,
								awaitingNextAction: false,
								aiGroupId: undoneSession.aiGroupId,
								experienceName: undoneSession.experienceName,
							};

							let updatedDeferredAnalytics = pluginState.deferredAnalytics;

							if (autoApplyAIChangesEnabled()) {
								/**
								 * If traceId is present in decoration plugin state, use it, otherwise use traceIds from common attributes.
								 * Currently, traceId is set in decoration plugin state only for freegen.
								 * For other prompts, it should be in common attributes.
								 */
								const traceIds = decorationPluginState?.traceId
									? [decorationPluginState.traceId]
									: pluginState.commonAttributes.traceIds;
								const promptVersion = fg('platform_editor_ai_promptversion_in_analytics')
									? decorationPluginState?.promptVersion
									: undefined;
								const actionedPayload = createUnifiedAnalyticsPayload({
									...pluginState.commonAttributes,
									action: ACTION.ACTIONED,
									attributes: {
										aiResultAction: 'accepted',
										aiActionedType: 'automaticallyInserted',
										wasRedone: true,
									},
									interactionID: undoneSession.aiSessionId,
									experienceName: undoneSession.experienceName,
									invokedFrom,
									aiGroupId: undoneSession.aiGroupId,
									...{ traceIds },
									...(promptVersion ? { promptVersion } : {}),
								});

								updatedDeferredAnalytics = {
									...pluginState.deferredAnalytics,
									toDefer: upsertDeferral(pluginState.deferredAnalytics.toDefer, {
										aiSessionId: undoneSession.aiSessionId,
										payload: actionedPayload,
										waitForSessionEnd: pluginState.deferredAnalytics.sessionState === 'inProgress',
									}),
								};
							}

							return {
								...finishImmediateReaction(pluginState, mockSession, 'redo', newState.doc, {
									removedChars: undoneSession.aiOutputChars,
									removedWords: undoneSession.aiOutputWords,
									touched: undoneSession.undoneRanges.length,
								}),
								lastUndoneSession: null, // Clear after redo
								deferredAnalytics: updatedDeferredAnalytics,
							};
						}
					} else {
						// Clear expired undone session
						return { ...pluginState, lastUndoneSession: null };
					}
				}

				return pluginState;
			},
		},

		appendTransaction: (_transactions, _oldState, newState) => {
			const ps = aiMonitorWatchmenPluginKey.getState(newState);
			const decorationPluginState = aiExperienceDecorationPluginKey.getState(newState);
			const pluginState = ps ?? initialState;
			const deferredAnalytics = pluginState.deferredAnalytics;

			const { toDefer, toCancel, toDrop, sessionState } = deferredAnalytics;

			if (toDrop) {
				window.clearTimeout(deferredAnalytics.deferred[toDrop]);
				return newState.tr.setMeta(META_DEFERRED_ANALYTICS, {
					...deferredAnalytics,
					deferred: omit(deferredAnalytics.deferred, [toDrop]),
					toDrop: undefined,
				});
			}

			const isReady = (entry: AnalyticsToDefer) =>
				!entry.waitForSessionEnd || sessionState === 'justEnded';
			const readyDeferrals = (toDefer ?? []).filter(isReady);

			if (readyDeferrals.length) {
				const stillWaiting = (toDefer ?? []).filter((entry) => !isReady(entry));
				const scheduled: Record<string, number> = {};

				for (const entry of readyDeferrals) {
					// Re-arming a backend session replaces its previous timer.
					const previousTimeoutId = deferredAnalytics?.deferred?.[entry.aiSessionId];
					if (previousTimeoutId !== undefined) {
						window.clearTimeout(previousTimeoutId);
					}

					scheduled[entry.aiSessionId] = window.setTimeout(
						() => api?.analytics?.actions.fireAnalyticsEvent(entry.payload),
						ACTIONED_EVENT_DELAY,
					);
				}

				const updatedDeferredAnalytics = {
					...deferredAnalytics,
					deferred: {
						...deferredAnalytics?.deferred,
						...scheduled,
					},
					toDefer: stillWaiting.length ? stillWaiting : undefined,
					// Backend deferrals do not participate in the frontend session lifecycle.
					sessionState: readyDeferrals.some((entry) => entry.waitForSessionEnd)
						? 'notStarted'
						: sessionState,
				};

				return newState.tr.setMeta(META_DEFERRED_ANALYTICS, updatedDeferredAnalytics);
			}

			if (toCancel) {
				if (expValEquals('platform_editor_discarded_unified_analytic_events', 'isEnabled', true)) {
					// Backend discards must not inherit attributes from an earlier frontend session.
					const discardedPayload = deferredAnalytics.toCancelIsBackendPersisted
						? createUnifiedAnalyticsPayload({
								action: ACTION.DISCARDED,
								interactionID: toCancel,
								experienceName: deferredAnalytics.toCancelExperienceName,
								aiGroupId: undefined,
							})
						: createUnifiedAnalyticsPayload({
								...pluginState.commonAttributes,
								action: ACTION.DISCARDED,
								interactionID: toCancel,
								experienceName:
									pluginState.commonAttributes.experienceName ??
									deferredAnalytics.toCancelExperienceName,
								invokedFrom: pluginState.commonAttributes.invokedFrom,
								aiGroupId: pluginState.commonAttributes.aiGroupId,
								...{
									traceIds: pluginState.commonAttributes.traceIds,
								},
								...(pluginState.commonAttributes.promptVersion &&
								fg('platform_editor_ai_promptversion_in_analytics')
									? {
											promptVersion: pluginState.commonAttributes.promptVersion,
										}
									: {}),
							});
					api?.analytics?.actions.fireAnalyticsEvent(discardedPayload);
				}

				window.clearTimeout(deferredAnalytics.deferred[toCancel]);

				const updatedDeferredAnalytics = {
					...deferredAnalytics,
					deferred: omit(deferredAnalytics.deferred, [toCancel]),
					toCancel: undefined,
					toCancelExperienceName: undefined,
					toCancelIsBackendPersisted: undefined,
				};

				return newState.tr.setMeta(META_DEFERRED_ANALYTICS, updatedDeferredAnalytics);
			}

			if (!ps?.pendingAnalytics) {
				return null;
			}

			const {
				aiSessionId,
				nextActionType,
				aiOutputChars,
				aiOutputWords,
				nextActionRemovedAiChars,
				nextActionRemovedAiWords,
				nextActionTouchedAiRanges,
				nextActionLatencyMs,
				aiGroupId,
				experienceName,
			} = ps.pendingAnalytics;

			const tr = newState.tr.setMeta(META_FLUSH_ANALYTICS, true);
			return addAnalytics({
				editorState: newState,
				tr,
				payload: {
					action: 'updated',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'aiImmediateReaction',
					attributes: {
						aiSessionId,
						aiGroupId,
						nextActionType,
						aiOutputChars,
						aiOutputWords,
						nextActionRemovedAiChars,
						nextActionRemovedAiWords,
						nextActionTouchedAiRanges,
						nextActionLatencyMs,
						traceId: decorationPluginState?.traceId,
						promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
							? decorationPluginState?.promptVersion
							: undefined,
						experienceName,
					},
					eventType: EVENT_TYPE.TRACK,
				},
				addToHistory: false,
			});
		},
	});
};
